/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { MotionConfig } from "motion/react";
import "./styles/fonts.css";
import "./index.css";
import "./styles/grid-home.css";
import "./styles/command-center.css";
import "./styles/mobile.css";
import "./styles/pages.css";
import "./styles/routers.css";
import "./styles/docker.css";
import "./styles/servers.css";
import "./styles/switches.css";
import "./styles/liquid-glass.css";
import "./styles/tooltips.css";
import "./styles/motion.css";
import "./styles/controls.css";
import { router } from "@/routes";
import { AuthProvider } from "@/context/auth";
import { TabNavigationProvider } from "@/context/tabNavigation";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/utils/language";
import { installSessionFetch } from "@/utils/sessionToken";
import { initAppearance } from "@/utils/appearance";
import { applyUiScale } from "@/utils/uiScale";
import { installScrollPad } from "@/utils/scrollPad";
import { installUiFeedback } from "@/utils/uiFeedback";

installSessionFetch();
initAppearance();
applyUiScale();
installUiFeedback();
window.addEventListener("DOMContentLoaded", installScrollPad, { once: true });
if (document.readyState !== "loading") installScrollPad();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 401/403 errors
        if (error instanceof Error && "status" in error) {
          const status = (error as { status: number }).status;
          if (status === 401 || status === 403) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <TabNavigationProvider>
              <TooltipProvider>
                <MotionConfig reducedMotion="user">
                  <RouterProvider router={router} />
                </MotionConfig>
              </TooltipProvider>
            </TabNavigationProvider>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
